import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, ArrowUpRight } from "lucide-react";
import { CompanyAvatar } from "@/components/CompanyAvatar";
import { api } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const WinnerItem = ({ year, c, dup }) => (
  <Link to={`/awards/${year}/ceremony#${slug(c.name)}`} tabIndex={dup ? -1 : 0} aria-hidden={dup || undefined}
    className="group flex shrink-0 items-center gap-4 pr-12" data-testid={dup ? undefined : `spotlight-winner-${slug(c.name)}`}>
    <Trophy className="h-5 w-5 text-white" />
    <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">{c.name}</span>
    {(c.winner.partners || []).length > 0 && (
      <span className="flex -space-x-2">
        {c.winner.partners.map((p) => <span key={p.id} className="rounded-full ring-2 ring-[#E0218A]"><CompanyAvatar c={p} size="h-8 w-8" text="text-sm" /></span>)}
      </span>
    )}
    <span className="font-display text-2xl text-white group-hover:underline underline-offset-4">{c.winner.title}</span>
    <span className="text-white/50">/</span>
  </Link>
);

export default function WinnersSpotlight() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/awards").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  const cats = Array.isArray(d?.categories) ? d.categories.filter((c) => c.winner) : [];
  const year = d?.year;
  if (!year) return null;

  if (!cats.length) {
    return (
      <section className="relative border-y border-[#E0218A]/40 bg-gradient-to-r from-[#B5157A] via-[#E0218A] to-[#8E2BC4]" data-testid="winners-spotlight-open">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-white">
          <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em]"><Trophy className="h-4 w-4" />Balqis Partners Awards {year}</span>
          <p className="font-display text-xl sm:text-2xl flex-1">Nominations are open — who built the partnership of the year?</p>
          <Link to="/awards" className="inline-flex items-center gap-1.5 self-start sm:self-auto rounded-full bg-[#FFF7FB] px-5 py-2.5 text-xs font-semibold text-[#8E2BC4] hover:bg-[#FBE9F3] transition-colors" data-testid="winners-spotlight-nominate">Nominate & vote<ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
      </section>
    );
  }

  const loop = cats.length < 3 ? [...cats, ...cats, ...cats] : cats;
  const dur = Math.max(30, loop.length * 9);
  return (
    <section className="relative overflow-hidden border-y border-[#E0218A]/40 bg-gradient-to-r from-[#B5157A] via-[#E0218A] to-[#8E2BC4]" data-testid="winners-spotlight">
      <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #fff 0, transparent 40%), radial-gradient(circle at 80% 50%, #fff 0, transparent 35%)" }} />
      <div className="relative flex flex-col lg:flex-row lg:items-center">
        <Link to={`/awards/${year}/ceremony`} className="z-10 flex shrink-0 items-center justify-between gap-3 bg-[#FFF7FB] px-5 sm:px-8 py-4 lg:py-5 text-[#8E2BC4] lg:[clip-path:polygon(0_0,100%_0,92%_100%,0_100%)] lg:pr-14" data-testid="winners-spotlight-ceremony-link">
          <span className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-b from-[#8E2BC4] to-[#B5157A] text-white"><Trophy className="h-4 w-4" /></span>
            <span>
              <span className="block text-[10px] uppercase tracking-[0.28em] text-slate-300">Balqis Partners Awards {year}</span>
              <span className="block font-display text-lg text-[#2A1240]">{cats.length} winner{cats.length === 1 ? "" : "s"} crowned</span>
            </span>
          </span>
          <ArrowUpRight className="h-4 w-4 lg:ml-3" />
        </Link>
        <div className="relative flex-1 overflow-hidden py-4 [mask-image:linear-gradient(90deg,transparent,#000_6%,#000_94%,transparent)]">
          <motion.div className="flex w-max" animate={{ x: ["0%", "-50%"] }} transition={{ duration: dur, ease: "linear", repeat: Infinity }} data-testid="winners-spotlight-track">
            {[0, 1].map((k) => (
              <div key={k} className="flex shrink-0 pl-10">
                {loop.map((c, i) => <WinnerItem key={`${k}-${i}`} year={year} c={c} dup={k === 1 || i >= cats.length} />)}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
